import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, X as XIcon, Truck } from "lucide-react";

/**
 * Phase AE — equipment subset extracted out of ``CreateRentalContractDialog``.
 * Renders ONLY the equipment fields + the AL1 Alvys lookup UX. Billing,
 * dates, rates, deposits, replacement value etc. stay in whichever
 * parent dialog needs them.
 *
 * Props:
 *   value         : object — current equipment slice (equipment_type, unit_number,
 *                   make, model, year, vin, plate, equipment_size,
 *                   alvys_trailer_id, alvys_synced_at, equipment_type_other).
 *   onChange(k,v) : update helper, called per-field.
 *   open          : the parent dialog's open flag — used to reset Alvys
 *                   debounce state when the dialog re-opens.
 *   testIdPrefix  : optional — prefix every data-testid so two dialogs
 *                   mounted in the same DOM tree don't collide. Default ""
 *                   keeps the existing CreateRentalContractDialog test ids.
 */
export const EQUIPMENT_TYPES = [
    { value: "reefer", label: "Reefer" },
    { value: "dry_van", label: "Dry Van" },
    { value: "conestoga", label: "Conestoga" },
    { value: "flatbed", label: "Flatbed" },
    { value: "step_deck", label: "Step Deck" },
    { value: "other", label: "Other" },
];

export function EquipmentSection({ value, onChange, open, testIdPrefix = "" }) {
    const tid = (k) => `${testIdPrefix}${k}`;

    // -------------------------------------------------------------
    // Phase AL1 — debounced Alvys trailer lookup on unit_number.
    // -------------------------------------------------------------
    // States: idle | checking | matched | applied | dismissed | no_match
    const [alvysState, setAlvysState] = useState("idle");
    const [alvysResult, setAlvysResult] = useState(null);
    const [alvysAppliedSnapshot, setAlvysAppliedSnapshot] = useState(null);
    const _alvysSeqRef = useRef(0);
    const _alvysSkipUnitsRef = useRef(new Set());

    useEffect(() => {
        if (!open) return;
        setAlvysState("idle");
        setAlvysResult(null);
        setAlvysAppliedSnapshot(null);
        _alvysSkipUnitsRef.current = new Set();
    }, [open]);

    useEffect(() => {
        const unit = (value.unit_number || "").trim();
        if (!open || unit.length < 2) {
            if (alvysState !== "applied") setAlvysState("idle");
            return undefined;
        }
        if (_alvysSkipUnitsRef.current.has(unit.toUpperCase())) return undefined;
        if (alvysState === "applied" && value.alvys_trailer_id &&
            (alvysAppliedSnapshot?.unit_number || "").toUpperCase() === unit.toUpperCase()) {
            return undefined;
        }
        const mySeq = ++_alvysSeqRef.current;
        const timer = setTimeout(async () => {
            setAlvysState("checking");
            try {
                const { data } = await api.get("/admin/alvys/trailers/lookup", {
                    params: { unit_number: unit },
                });
                if (mySeq !== _alvysSeqRef.current) return;
                if (!data || data.enabled === false) {
                    setAlvysState("idle");
                    setAlvysResult(null);
                    return;
                }
                if (data.matched) {
                    setAlvysResult({ ...data, _looked_up_unit: unit });
                    setAlvysState("matched");
                } else {
                    setAlvysState("no_match");
                    setAlvysResult(null);
                }
            } catch (_) {
                if (mySeq === _alvysSeqRef.current) {
                    setAlvysState("idle");
                    setAlvysResult(null);
                }
            }
        }, 600);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.unit_number, open]);

    const applyAlvysPrefill = () => {
        if (!alvysResult || alvysResult.matched !== true) return;
        const p = alvysResult.prefill || {};
        const nowIso = new Date().toISOString();
        // Apply each prefilled field via the parent's onChange so the
        // controlled component contract stays clean.
        if (p.year) onChange("year", String(p.year));
        if (p.make) onChange("make", p.make);
        if (p.vin) onChange("vin", p.vin);
        if (p.plate) onChange("plate", p.plate);
        if (p.equipment_type) onChange("equipment_type", p.equipment_type);
        if (p.equipment_size) onChange("equipment_size", p.equipment_size);
        onChange("alvys_trailer_id", alvysResult.alvys_id || "");
        onChange("alvys_synced_at", nowIso);
        setAlvysAppliedSnapshot({
            unit_number: alvysResult._looked_up_unit,
            applied_prefill: { ...p },
            synced_at: nowIso,
            alvys_status: alvysResult.alvys_status,
        });
        setAlvysState("applied");
        toast.success("Prefilled from Alvys");
    };

    const dismissAlvysBanner = () => {
        const unit = (value.unit_number || "").trim().toUpperCase();
        if (unit) _alvysSkipUnitsRef.current.add(unit);
        setAlvysState("dismissed");
        setAlvysResult(null);
        // Phase AE — also clear the parent's applied-provenance so the
        // submit payload doesn't carry a stale Alvys link. Matches the
        // pre-refactor behaviour (CreateRentalContractDialog used to do
        // this inline on dismiss).
        onChange("alvys_trailer_id", "");
        onChange("alvys_synced_at", "");
        setAlvysAppliedSnapshot(null);
    };

    const isPrefillEdited = (() => {
        if (alvysState !== "applied" || !alvysAppliedSnapshot) return false;
        const snap = alvysAppliedSnapshot.applied_prefill || {};
        const cur = {
            year: value.year ? String(value.year) : "",
            make: value.make || "",
            vin: value.vin || "",
            plate: value.plate || "",
            equipment_type: value.equipment_type || "",
            equipment_size: value.equipment_size || "",
        };
        for (const k of Object.keys(snap)) {
            const snapVal = k === "year" ? String(snap[k]) : (snap[k] || "");
            if ((cur[k] || "") !== snapVal) return true;
        }
        return false;
    })();

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
                {EQUIPMENT_TYPES.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => onChange("equipment_type", t.value)}
                        data-testid={tid(`equipment-${t.value}`)}
                        className={
                            "rounded-md px-3 py-2 text-sm font-medium ring-1 ring-inset transition-colors " +
                            (value.equipment_type === t.value
                                ? "bg-blue-50 text-blue-900 ring-blue-300"
                                : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")
                        }
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            {value.equipment_type === "other" && (
                <Input
                    value={value.equipment_type_other || ""}
                    onChange={(e) => onChange("equipment_type_other", e.target.value)}
                    placeholder="Specify equipment"
                    data-testid={tid("rental-eq-other")}
                />
            )}
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                    <div className="flex items-center justify-between">
                        <Label>Unit number *</Label>
                        {alvysState === "checking" && (
                            <span
                                data-testid={tid("alvys-checking-indicator")}
                                className="inline-flex items-center gap-1.5 text-xs text-slate-500"
                            >
                                <Loader2 size={12} className="animate-spin" />
                                Checking Alvys…
                            </span>
                        )}
                    </div>
                    <Input
                        value={value.unit_number || ""}
                        onChange={(e) => onChange("unit_number", e.target.value)}
                        data-testid={tid("rental-unit")}
                    />
                    {alvysState === "matched" && alvysResult?.matched && (
                        <div
                            data-testid={tid("alvys-lookup-banner")}
                            className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 flex flex-wrap items-start gap-x-3 gap-y-2"
                        >
                            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-emerald-700" />
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-medium" data-testid={tid("alvys-banner-summary")}>
                                    Found in Alvys:{" "}
                                    {[
                                        alvysResult.prefill?.year,
                                        alvysResult.prefill?.make,
                                        alvysResult.alvys_equipment_type_raw,
                                        alvysResult.prefill?.equipment_size,
                                    ].filter(Boolean).join(" · ")}
                                </div>
                                <div className="text-xs opacity-90">
                                    {alvysResult.prefill?.vin && (
                                        <span>VIN {String(alvysResult.prefill.vin).slice(0, 6)}…{String(alvysResult.prefill.vin).slice(-4)} · </span>
                                    )}
                                    Alvys status: <strong>{alvysResult.alvys_status || "—"}</strong>
                                    {alvysResult.source === "cache" && (
                                        <span className="opacity-70"> · cached</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 ml-auto">
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={applyAlvysPrefill}
                                    data-testid={tid("alvys-use-this-data-btn")}
                                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-7 px-2.5 text-xs"
                                >
                                    Use this data
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={dismissAlvysBanner}
                                    data-testid={tid("alvys-dismiss-btn")}
                                    className="h-7 px-2 text-xs text-emerald-900 hover:bg-emerald-100"
                                >
                                    Dismiss
                                </Button>
                            </div>
                        </div>
                    )}
                    {alvysState === "applied" && value.alvys_trailer_id && (
                        <div
                            data-testid={tid("alvys-synced-pill")}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-900"
                        >
                            <Truck size={12} />
                            Prefilled from Alvys
                            {isPrefillEdited
                                ? <span className="opacity-80">· edited</span>
                                : <span className="opacity-80">· last synced just now</span>}
                            <button
                                type="button"
                                onClick={dismissAlvysBanner}
                                data-testid={tid("alvys-synced-pill-dismiss")}
                                className="ml-1 text-emerald-700 hover:text-emerald-900"
                                aria-label="Dismiss Alvys sync"
                            >
                                <XIcon size={10} />
                            </button>
                        </div>
                    )}
                </div>
                <div className="space-y-1.5">
                    <Label>VIN</Label>
                    <Input value={value.vin || ""} onChange={(e) => onChange("vin", e.target.value)} data-testid={tid("rental-vin")} />
                </div>
                <div className="space-y-1.5">
                    <Label>Make</Label>
                    <Input value={value.make || ""} onChange={(e) => onChange("make", e.target.value)} data-testid={tid("rental-make")} />
                </div>
                <div className="space-y-1.5">
                    <Label>Model</Label>
                    <Input value={value.model || ""} onChange={(e) => onChange("model", e.target.value)} data-testid={tid("rental-model")} />
                </div>
                <div className="space-y-1.5">
                    <Label>Year</Label>
                    <Input type="number" value={value.year || ""} onChange={(e) => onChange("year", e.target.value)} data-testid={tid("rental-year")} />
                </div>
                <div className="space-y-1.5">
                    <Label>Plate</Label>
                    <Input value={value.plate || ""} onChange={(e) => onChange("plate", e.target.value)} data-testid={tid("rental-plate")} />
                </div>
                <div className="space-y-1.5">
                    <Label>Equipment size</Label>
                    <Input
                        value={value.equipment_size || ""}
                        onChange={(e) => onChange("equipment_size", e.target.value)}
                        placeholder="e.g. 53'"
                        data-testid={tid("rental-equipment-size")}
                    />
                </div>
            </div>
        </div>
    );
}
